/** Suffix used for the draft key before {@code /me} has resolved, or when signed out. */
const ANON_SUFFIX = ":anon";

/**
 * Whether a change of draft storage key means a different person is now using the app, and the
 * in-memory draft must therefore be replaced by whatever is stored under the new key.
 *
 * <p>Drafts are keyed per user so one host's in-progress event can never leak into another's
 * session. But the anonymous key is not a user — it is the placeholder used while auth is still
 * hydrating. On any full page load the key goes {@code anon → real id} for the same visitor, and
 * reloading there would throw away anything written to state in the meantime.
 *
 * <p>That is not hypothetical: returning from Zoom OAuth, the create-event page selects Zoom on
 * mount while {@code /me} is still in flight. Treating the subsequent key change as a user switch
 * re-read the stored draft and undid the selection, so the card sat unselected even though the
 * badge reported Zoom connected.
 *
 * <p>Signing *out* (real id → anon) still reloads, so the next visitor on a shared machine does
 * not inherit the previous one's draft.
 */
export function shouldReloadDraftForKeyChange(previousKey: string, nextKey: string): boolean {
  if (previousKey === nextKey) {
    return false;
  }
  return !previousKey.endsWith(ANON_SUFFIX);
}
